import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Search, Users, X } from 'lucide-react';

// Multi-select of coaches shown as removable chips (Create Lesson Plan).
export default function CoachChipsSelect({ id, coaches, value, onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (event.type === 'keydown' && event.key !== 'Escape') return;
      if (event.type === 'mousedown' && wrapRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
    };
  }, [open]);

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const selected = value
    .map((coachId) => coaches.find((coach) => coach._id === coachId) || { _id: coachId, name: 'Coach' });
  const toggle = (coachId) =>
    onChange(value.includes(coachId) ? value.filter((idValue) => idValue !== coachId) : [...value, coachId]);
  const query = search.trim().toLowerCase();
  const visible = query ? coaches.filter((coach) => coach.name.toLowerCase().includes(query)) : coaches;

  return (
    <div ref={wrapRef} className={`lp-chips${disabled ? ' is-disabled' : ''}`}>
      <div className="lp-control lp-chips-control">
        <Users size={18} className="lp-control-icon" aria-hidden="true" />
        <div className="lp-chips-list">
          {selected.length === 0 && <span className="lp-placeholder">Select coaches</span>}
          {selected.map((coach) => (
            <span key={coach._id} className="lp-chip">
              {coach.name}
              {!disabled && (
                <button
                  type="button"
                  className="lp-chip-remove"
                  onClick={() => toggle(coach._id)}
                  aria-label={`Remove ${coach.name}`}
                >
                  <X size={12} aria-hidden="true" />
                </button>
              )}
            </span>
          ))}
        </div>
        <button
          type="button"
          id={id}
          className="lp-chips-toggle"
          onClick={() => setOpen((current) => !current)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label="Choose coaches"
          disabled={disabled}
        >
          <ChevronDown size={16} aria-hidden="true" />
        </button>
      </div>
      {open && (
        <div className="lp-chips-panel">
          <label className="lp-chips-search">
            <Search size={14} aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search Coach"
              aria-label="Search Coach"
              autoFocus
            />
          </label>
          <div role="group" aria-label="Coaches" className="lp-chips-options">
            {visible.length === 0 && <p className="lp-muted">No coach found</p>}
            {visible.map((coach) => {
              const checked = value.includes(coach._id);
              return (
                <label key={coach._id} className={`lp-chips-option${checked ? ' is-checked' : ''}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggle(coach._id)} />
                  <span>{coach.name}</span>
                  {checked && <Check size={14} aria-hidden="true" />}
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
